import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { scrollGreenEnergyCarouselToCenter } from '../../utils/greenEnergyEcoCard';
import {
  GREEN_ENERGY_FLOW_BUBBLE_COUNT,
  greenEnergyFlowBubbleStyle,
  greenEnergyFlowsNearlyEqual,
  measureGreenEnergyBottomRouteFlowPx,
} from '../../utils/greenEnergyFlow';
import EmsBatteryEcoCard from '../../components/EmsBatteryEcoCard/EmsBatteryEcoCard';
import EmsSolarEcoCard from '../../components/EmsSolarEcoCard/EmsSolarEcoCard';
import '../../components/greenEnergyFlow.css';

export default function StartPageEmsCarousel({
  isEmsBound,
  isEmsLoading = false,
  insolationTodayPct,
  insolationTomorrowPct,
  pvPowerW,
  socPercent,
  batteryCapacityKwh,
  batteryPowerW,
  socPriceTiers = null,
  latitude = null,
  longitude = null,
  openEmsInverterUrl = null,
  showBatteryPower = true,
  children,
}) {
  const { t } = useTranslation();
  const carouselRef = useRef(null);
  const solarIconRef = useRef(null);
  const batteryIconRef = useRef(null);
  const [solarToBatteryFlowPx, setSolarToBatteryFlowPx] = useState(null);

  const showEmsCarousel = isEmsBound || isEmsLoading;
  const showTelemetryLoading = isEmsLoading;

  const pvKw = pvPowerW != null ? Math.max(0, Number(pvPowerW)) / 1000 : null;
  const batteryKw = batteryPowerW != null ? Number(batteryPowerW) / 1000 : null;
  const showSolarToBatteryFlow =
    !showTelemetryLoading && pvKw != null && pvKw > 0.05 && batteryKw != null && batteryKw < -0.05;

  const measureSolarToBatteryFlow = useCallback(() => {
    if (!showSolarToBatteryFlow) {
      setSolarToBatteryFlowPx(null);
      return;
    }
    const mainCard = carouselRef.current?.querySelector('.green-energy-main-card');
    const next = measureGreenEnergyBottomRouteFlowPx(solarIconRef.current, batteryIconRef.current, mainCard);
    if (!next) {
      setSolarToBatteryFlowPx(null);
      return;
    }
    setSolarToBatteryFlowPx(prev => (greenEnergyFlowsNearlyEqual(prev, next) ? prev : next));
  }, [showSolarToBatteryFlow]);

  const scrollCarouselToCenter = useCallback(() => {
    scrollGreenEnergyCarouselToCenter(carouselRef.current);
    queueMicrotask(() => measureSolarToBatteryFlow());
  }, [measureSolarToBatteryFlow]);

  useLayoutEffect(() => {
    if (!showEmsCarousel) return undefined;
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => scrollCarouselToCenter());
    });
    return () => window.cancelAnimationFrame(id);
  }, [showEmsCarousel, scrollCarouselToCenter]);

  useEffect(() => {
    if (!showEmsCarousel) return undefined;
    const root = carouselRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => scrollCarouselToCenter());
    ro.observe(root);
    window.addEventListener('orientationchange', scrollCarouselToCenter);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', scrollCarouselToCenter);
    };
  }, [showEmsCarousel, scrollCarouselToCenter]);

  useLayoutEffect(() => {
    if (!showEmsCarousel) {
      setSolarToBatteryFlowPx(null);
      return undefined;
    }
    let raf = 0;
    const run = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => measureSolarToBatteryFlow());
    };
    run();
    window.addEventListener('resize', run);
    window.addEventListener('scroll', run, true);
    const carousel = carouselRef.current;
    carousel?.addEventListener('scroll', run);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', run);
      window.removeEventListener('scroll', run, true);
      carousel?.removeEventListener('scroll', run);
    };
  }, [showEmsCarousel, measureSolarToBatteryFlow, showSolarToBatteryFlow, pvPowerW, batteryPowerW]);

  if (!showEmsCarousel) {
    return children;
  }

  return (
    <div className="start-page-ems-carousel-outer">
      {solarToBatteryFlowPx &&
        Array.from({ length: GREEN_ENERGY_FLOW_BUBBLE_COUNT }, (_, i) => (
          <span
            key={`start-ems-flow-solar-bat-${i}`}
            className="green-energy-flow-bubble-fixed green-energy-flow-bubble-fixed--solar green-energy-flow-bubble-fixed--bottom-route"
            aria-hidden
            style={greenEnergyFlowBubbleStyle(solarToBatteryFlowPx, i, { bottomRoute: true })}
          />
        ))}

      <div ref={carouselRef} className="green-energy-carousel start-page-ems-carousel" aria-label={t('renewable')}>
        <EmsSolarEcoCard
          loading={showTelemetryLoading}
          insolationTodayPct={insolationTodayPct}
          insolationTomorrowPct={insolationTomorrowPct}
          pvPowerW={pvPowerW}
          latitude={latitude}
          longitude={longitude}
          iconRef={solarIconRef}
        />

        {children}

        <EmsBatteryEcoCard
          loading={showTelemetryLoading}
          socPercent={socPercent}
          batteryCapacityKwh={batteryCapacityKwh}
          batteryPowerW={batteryPowerW}
          socPriceTiers={socPriceTiers}
          openEmsInverterUrl={openEmsInverterUrl}
          showPower={showBatteryPower}
          iconRef={batteryIconRef}
        />
      </div>
      <p className="ems-station-source-hint">{t('stationAdditionalSourceBess')}</p>
    </div>
  );
}
